import React from 'react';
import { Box, Button, Grid, Paper, Stack, Typography } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import CollectionsIcon from '@mui/icons-material/Collections';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import ArtTrackOutlinedIcon from '@mui/icons-material/ArtTrackOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';

import { CARD_SIZES } from '../utils';

const ACCENT = '#004591';
const SELECTED = '#2e7d32';

const ICONS = {
  folder: FolderOutlinedIcon,
  video: PlayCircleOutlineRoundedIcon,
  collection: CollectionsIcon,
  structure: AccessibilityIcon,
  laminaria: ArtTrackOutlinedIcon,
  section: PhotoLibraryOutlinedIcon,
  image: ImageOutlinedIcon,
};

/**
 * Card genérico y reutilizable para el listado de recursos.
 * Basado en el diseño de los cards de Videos (barra de acento, hover, CTA).
 * Muestra la imagen del recurso cuando existe; si no, un ícono según su tipo.
 *
 * Props:
 * - size: 'small' | 'medium' | 'large'. Cambia el tamaño del card y de la imagen.
 * - onClick: acción principal del card (entrar a un contenedor o ver el detalle).
 * - mode: 'view' | 'select'. En 'select' las hojas muestran el botón de seleccionar.
 * - selectable: si el item admite selección (solo hojas / recursos finales).
 * - isSelected: si el item está seleccionado (cambia el estilo y el botón toggle).
 * - onSelect: callback (sin args) para alternar la selección de este item.
 */
const ResourceCard = ({
  size = 'medium',
  title,
  image,
  icon,
  kindLabel,
  ctaLabel,
  onClick,
  mode = 'view',
  selectable = false,
  isSelected = false,
  onSelect,
}) => {
  const IconComponent = ICONS[icon] || FolderOutlinedIcon;
  const showSelect = mode === 'select' && selectable;
  const stylesBySize = CARD_SIZES[size] || CARD_SIZES.medium;

  return (
    <Grid item {...stylesBySize.grid}>
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: stylesBySize.radius,
          overflow: 'hidden',
          border: isSelected ? '2px solid' : '1px solid',
          borderColor: isSelected ? SELECTED : '0.5px solid #e0e0e0',
          backgroundColor: isSelected ? `${SELECTED}0A` : '#ffffff',
          transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: stylesBySize.accentBar,
            backgroundColor: isSelected ? SELECTED : ACCENT,
            transform: isSelected ? 'scaleX(1)' : 'scaleX(0)',
            transformOrigin: 'left',
            transition: 'transform 0.3s ease',
            zIndex: 1,
          },
          '&:hover .resource-icon': {
            transform: 'scale(1.08)',
            backgroundColor: ACCENT,
            color: '#ffffff',
          },
        }}>
        {isSelected && (
          <CheckCircleRoundedIcon
            sx={{
              position: 'absolute',
              fontSize: stylesBySize.checkIconSize,
              top: stylesBySize.checkOffset,
              right: stylesBySize.checkOffset,
              zIndex: 2,
              color: SELECTED,
              backgroundColor: '#ffffff',
              borderRadius: '50%',
            }}
          />
        )}
        {image && (
          <Box
            component='img'
            src={image}
            alt={title}
            sx={{
              width: '100%',
              height: stylesBySize.imageHeight,
              objectFit: 'cover',
              backgroundColor: 'action.hover',
            }}
          />
        )}
        <Stack
          direction='column'
          spacing={stylesBySize.stackSpacing}
          alignItems='center'
          p={stylesBySize.padding}
          sx={{ flexGrow: 1 }}>
          {!image && (
            <Box
              className='resource-icon'
              sx={{
                width: stylesBySize.iconBox,
                height: stylesBySize.iconBox,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: ACCENT,
                backgroundColor: `${ACCENT}18`,
                transition: 'transform 0.25s ease, background-color 0.25s ease, color 0.25s ease',
              }}>
              <IconComponent sx={{ fontSize: stylesBySize.iconFontSize }} />
            </Box>
          )}
          {kindLabel && (
            <Typography
              variant='caption'
              fontWeight={700}
              sx={{
                color: ACCENT,
                fontSize: `${stylesBySize.kindFontSize}rem`,
                letterSpacing: stylesBySize.letterSpacing,
                textTransform: 'uppercase',
              }}>
              {kindLabel}
            </Typography>
          )}
          <Typography
            variant={stylesBySize.titleVariant}
            fontWeight='bold'
            color='primary'
            textAlign='center'
            sx={{
              flexGrow: 1,
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: stylesBySize.titleLines,
              overflow: 'hidden',
            }}>
            {title}
          </Typography>
          <Stack
            direction={stylesBySize.buttonDirection}
            spacing={1}
            sx={{ mt: 1, width: '100%' }}
            justifyContent='center'>
            <Button
              size={stylesBySize.buttonSize}
              className='resource-cta'
              variant='contained'
              disableElevation
              endIcon={stylesBySize.showCtaIcon ? <ArrowForwardRoundedIcon /> : null}
              onClick={onClick}
              sx={{
                flex: showSelect ? 1 : 'initial',
                borderRadius: stylesBySize.buttonRadius,
                textTransform: 'none',
                fontWeight: 600,
                px: stylesBySize.buttonPx,
                color: '#ffffff !important',
                backgroundColor: ACCENT,
                transition: 'gap 0.2s ease, background-color 0.2s ease',
                '&:hover': {
                  color: '#ffffff !important',
                  backgroundColor: ACCENT,
                  filter: 'brightness(0.92)',
                },
              }}>
              {ctaLabel || 'Ver'}
            </Button>
            {showSelect && (
              <Button
                size={stylesBySize.buttonSize}
                variant={isSelected ? 'outlined' : 'contained'}
                disableElevation
                onClick={() => onSelect?.()}
                sx={{
                  flex: 1,
                  borderRadius: stylesBySize.buttonRadius,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: stylesBySize.buttonPx,
                  ...(isSelected
                    ? {
                        color: `${SELECTED} !important`,
                        borderColor: SELECTED,
                        '&:hover': {
                          color: `${SELECTED} !important`,
                          borderColor: SELECTED,
                          backgroundColor: `${SELECTED}0A`,
                        },
                      }
                    : {
                        color: '#ffffff !important',
                        backgroundColor: SELECTED,
                        '&:hover': {
                          color: '#ffffff !important',
                          backgroundColor: SELECTED,
                          filter: 'brightness(0.92)',
                        },
                      }),
                }}>
                {isSelected ? 'Quitar' : 'Seleccionar'}
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>
    </Grid>
  );
};

export default ResourceCard;
